/**
 * Full-text search index for all documentation pages.
 *
 * Flattens the `docsContent` data file plus the two custom pages
 * (quick-start, google-calendar) into a single searchable list, then
 * builds a FlexSearch Document index over title, category, and body.
 *
 * Usage:
 *   import { search } from './docsSearch';
 *   const hits = search('how to create a job');
 *   // hits = [{ slug, title, category, snippet, url }, ...]
 */

import FlexSearch from 'flexsearch';
import docsContent from './docsContent';

/* -------- Flatten content into searchable text per page -------- */

function sectionsToText(sections) {
  const parts = [];
  for (const s of sections) {
    switch (s.type) {
      case 'intro':
        parts.push(s.body);
        break;
      case 'steps':
        if (s.title) parts.push(s.title);
        s.items.forEach(i => parts.push(`${i.title}. ${i.body}`));
        break;
      case 'list':
        if (s.title) parts.push(s.title);
        s.items.forEach(i => parts.push(i));
        break;
      case 'bullets':
        if (s.title) parts.push(s.title);
        s.items.forEach(i => parts.push(`${i.title}. ${i.body}`));
        break;
      case 'tip':
        parts.push(s.body);
        break;
      case 'troubleshoot':
        s.items.forEach(i => parts.push(`${i.q}. ${i.a}`));
        break;
      case 'formMock':
        if (s.title) parts.push(s.title);
        if (s.subtitle) parts.push(s.subtitle);
        s.fields?.forEach(f => parts.push(`${f.label}: ${f.value}`));
        break;
      case 'panelMock':
        if (s.title) parts.push(s.title);
        s.rows?.forEach(r => parts.push(`${r.label}: ${r.value}`));
        break;
      default:
        break;
    }
  }
  return parts.join('\n');
}

/* -------- The two custom pages (content mirrored for search only) -------- */

const customPages = {
  'quick-start': {
    title: 'Quick Start Guide',
    category: 'Getting Started',
    description: 'Get started with MoveMate in under 10 minutes.',
    body: [
      'Before You Start business email company logo team member emails pricing basics hourly rates per-mile charge VAT materials',
      'Welcome to MoveMate all-in-one platform for managing moving jobs inventory customers teams',
      'Step 1 Create Your Company Account Register your company with name email password verify email log in dashboard',
      'Step 2 Configure Company Settings logo address phone business hours service area currency branding',
      'Step 3 Set Up Pricing VAT deposit quote validity per-mile rates labour hourly rates porters drivers supervisors materials boxes tape packing supplies',
      'Step 4 Add Team Members Admin Driver Porter Sales Rep invite staff mobile app',
      'Step 5 Create Your First Job customer contact pickup delivery addresses move date inventory AI scanning estimate team members',
      'Step 6 Connect Google Calendar optional sync jobs automatically',
      'Common Setup Issues verification email spam logo blurry invitation expired calendar not syncing address autocomplete Google Maps API key',
    ].join('\n'),
  },
  'google-calendar': {
    title: 'Google Calendar Integration',
    category: 'Integrations',
    description: 'Connect Google Calendar and auto-sync moving jobs.',
    body: [
      'Overview MoveMate integrates with Google Calendar to automatically sync moving jobs created updated',
      'Auto-Sync jobs sync automatically when created updated Live Updates Secure minimum-scope OAuth encrypted token storage',
      'How to Connect Open Company Settings find Google Calendar section click Connect sign in grant permission',
      'What Gets Synced job reference number customer name move date time pickup delivery address duration team members status',
      'NOT Synced customer phone email payment information detailed inventory internal notes pricing',
      'Managing Connection refresh disconnect stop future syncing',
      'Privacy Security authorize calendars create read update MoveMate events do not read personal events encrypted tokens revoke access',
      'Troubleshooting events not appearing connection disconnecting wrong calendar duplicating events primary calendar',
    ].join('\n'),
  },
};

/* -------- Build the flat entry list -------- */

export const docEntries = [
  ...Object.entries(customPages).map(([slug, p]) => ({
    slug,
    url: `/docs/${slug}`,
    title: p.title,
    category: p.category,
    description: p.description,
    body: p.body,
  })),
  ...Object.entries(docsContent).map(([slug, p]) => ({
    slug,
    url: `/docs/${slug}`,
    title: p.title,
    category: p.category,
    description: p.description,
    body: sectionsToText(p.sections),
  })),
];

/* -------- Build the FlexSearch Document index once -------- */

const index = new FlexSearch.Document({
  tokenize: 'forward',       // Matches partial words as user types
  cache: 100,                // Small LRU cache
  document: {
    id: 'slug',
    index: [
      { field: 'title', tokenize: 'forward', resolution: 9 },
      { field: 'category', tokenize: 'forward', resolution: 5 },
      { field: 'description', tokenize: 'forward', resolution: 4 },
      { field: 'body', tokenize: 'forward', resolution: 3 },
    ],
    store: ['title', 'category', 'description', 'body', 'url'],
  },
});

docEntries.forEach(e => index.add(e));

/* -------- Search + snippet helper -------- */

/**
 * Return a short snippet from `body` that contains the query, with
 * a leading/trailing ellipsis if the match is mid-text.
 */
function makeSnippet(body, query, max = 160) {
  if (!body) return '';
  const q = query.trim().toLowerCase();
  if (!q) return body.slice(0, max);
  const lower = body.toLowerCase();
  const i = lower.indexOf(q);
  if (i === -1) return body.slice(0, max) + (body.length > max ? '…' : '');

  // Window around the match
  const start = Math.max(0, i - 40);
  const end = Math.min(body.length, i + q.length + 120);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < body.length ? '…' : '';
  return prefix + body.slice(start, end) + suffix;
}

export function search(query, limit = 10) {
  const q = (query || '').trim();
  if (!q) return [];

  // FlexSearch returns results grouped by field. Merge and dedupe by slug.
  const raw = index.search(q, { enrich: true, limit });
  const seen = new Set();
  const hits = [];
  for (const group of raw) {
    for (const r of group.result) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      const d = r.doc || {};
      hits.push({
        slug: r.id,
        url: d.url,
        title: d.title,
        category: d.category,
        description: d.description,
        snippet: makeSnippet(d.body, q),
      });
      if (hits.length >= limit) return hits;
    }
  }
  return hits;
}

export default { search, docEntries };
